import { useQueryParams } from "@/shared/hooks";
import { useSelectedControlStore } from "@/shared/stores/selected-control.store";
import type {
  ControlParameterResponseDto,
  ControlResultColumnResponseDto,
  ControlResultListItemResponseDto,
} from "@/spz-api/model";
import type { RowSelectionState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useCallback } from "react";
import { useControlResultDetailsSheet } from "./use-control-result-details.sheet";
import { useControlResultsParams } from "./use-control-results-params";

interface UseControlResultsSelectionProps {
  controlId: string;
  controlInformation: {
    name: string;
    currency: string;
    parameters: ControlParameterResponseDto[];
    controlResultColumns: ControlResultColumnResponseDto[];
  };
  companyId: string;
  loaded: boolean;
  getRowById: (id: string) => ControlResultListItemResponseDto | undefined;
}

export const useControlResultsSelection = ({
  controlId,
  controlInformation,
  companyId,
  loaded,
  getRowById,
}: UseControlResultsSelectionProps) => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { setQueryParam } = useQueryParams();
  const controlResultsParams = useControlResultsParams();

  const { setSelectedControl, clearSelectedControl } =
    useSelectedControlStore();

  useEffect(() => {
    setSelectedControl({
      id: controlId,
      descriptorLabelKeyName: controlInformation.name,
      currency: controlInformation.currency,
      parameters: controlInformation.parameters,
      controlResultColumns: controlInformation.controlResultColumns,
    });

    return () => {
      clearSelectedControl();
    };
  }, [
    controlId,
    setSelectedControl,
    clearSelectedControl,
    controlInformation.name,
    controlInformation.currency,
    controlInformation.parameters,
    controlInformation.controlResultColumns,
  ]);

  const activeControlResult = controlResultsParams.activeControlResultId
    ? getRowById(controlResultsParams.activeControlResultId)
    : null;

  const resultGroupId = activeControlResult?.resultGroupId || "";

  const controlResultDetailsSheet = useControlResultDetailsSheet({
    controlId,
    resultGroupId,
    companyId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: both openControlActivitySheet and deleteQueryParam change on every render
  useEffect(() => {
    if (
      !controlResultDetailsSheet.autoOpen ||
      !loaded ||
      !controlResultsParams.activeControlResultId
    ) {
      return;
    }

    setRowSelection({ [controlResultsParams.activeControlResultId]: true });
    controlResultDetailsSheet.open(controlResultsParams.activeControlResultId);
  }, [
    controlResultDetailsSheet.autoOpen,
    loaded,
    controlResultsParams.activeControlResultId,
  ]);

  const handleRowClick = useCallback(
    (row: ControlResultListItemResponseDto) => {
      setQueryParam("r", row.id);
    },
    [setQueryParam],
  );

  return {
    rowSelection,
    setRowSelection,
    handleRowClick,
  };
};
